import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setFlashToast } from '../../../utils/flashToast';
import * as publicPetitionService from '../../../services/publicPetitionCaseRequests';
import { getProfile, type ExtranetUserProfile } from '../../../services/userProfile';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../../utils/requiredValidation';
import { usePublicPetitionWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { CriminalWizardDraft } from '../../../types/criminalCaseRequest';
import { usePublicPetitionLookups } from './usePublicPetitionLookups';
import { formatDisplayDate } from '../../../utils/dateFormat';
import { CriminalReviewDocumentsTable } from '../criminal/CriminalReviewDocumentsTable';
import { apiOrLookupLabel, deliveryMethodDisplay } from '../../view-request/viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../criminal/CriminalReviewUi';
import CommonCoverNoteModal from '../../../components/wizard/CommonCoverNoteModal';
import { PublicPetitionWizardActions } from './PublicPetitionWizardActions';
import { publicPetitionWizardPath } from './publicPetitionWizardShell';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';

function yesNoLabel(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

function caseMatterLabel(v: string): string {
  if (v === 'CRIMINAL') return 'Criminal Matter';
  if (v === 'CIVIL') return 'Civil Matter';
  return '—';
}

export default function PublicPetitionReviewPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { roleCode } = useRole();
  const tasksListPath = tasksListPathForRole(roleCode);
  const { draft, setDraft } = usePublicPetitionWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const [profile, setProfile] = useState<ExtranetUserProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCoverNote, setShowCoverNote] = useState(false);
  const [coverNoteRequestId, setCoverNoteRequestId] = useState('');
  const [coverNoteFileRef, setCoverNoteFileRef] = useState('');
  const lookups = usePublicPetitionLookups('review');

  useEffect(() => {
    let mounted = true;
    void getProfile().then((r) => {
      if (mounted && r.success) setProfile(r.data);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const deliveryLabel = useMemo(
    () => deliveryMethodDisplay(draft as unknown as CriminalWizardDraft, lookups.deliveryMethods),
    [draft, lookups.deliveryMethods],
  );

  const connectedLabel = yesNoLabel(draft.hasConnectedMatters);

  const submit = async () => {
    if (submitting) return;

    const specs: RequiredFieldSpec[] = [
      { key: 'confirmCorrect', label: 'Confirmation', value: draft.confirmCorrect ? 'yes' : '', elementId: 'pp-confirm' },
    ];
    const reqErrs = validateRequired(specs);
    setErrors(reqErrs);
    if (Object.keys(reqErrs).length) {
      focusFirstInvalid(specs, reqErrs);
      return;
    }

    setSubmitting(true);
    const r = await publicPetitionService.savePublicPetitionRequest(draft, {
      isDraft: false,
      allSteps: true,
      confirmedCorrect: true,
    });
    if (!r.success) {
      setSubmitting(false);
      setFlashToast({ message: r.message ?? 'Submission failed.', variant: 'error' });
      return;
    }
    setDraft((d) => ({
      ...d,
      draftRequestId: r.data.requestId,
      referenceNo: r.data.fileReferenceNumber ?? d.referenceNo,
    }));
    setFlashToast({ message: 'Case Request submitted successfully.', variant: 'success' });
    setCoverNoteRequestId(r.data.requestId);
    setCoverNoteFileRef(r.data.fileReferenceNumber ?? draft.referenceNo ?? '');
    setShowCoverNote(true);
    setSubmitting(false);
  };

  const closeCoverNote = () => {
    setShowCoverNote(false);
    setCoverNoteRequestId('');
    setCoverNoteFileRef('');
    nav(tasksListPath);
  };

  const reviewCrumbTo = publicPetitionWizardPath(1, draft.draftRequestId);
  const editTo = (step: 1 | 2 | 3 | 4 | 5) => publicPetitionWizardPath(step, draft.draftRequestId);

  return (
    <div className="ccr-review-page">
      <CommonCoverNoteModal
        isOpen={showCoverNote}
        caseRequestId={coverNoteRequestId}
        fileRefFallback={coverNoteFileRef}
        showSuccessBanner
        showCaseSource={false}
        onClose={closeCoverNote}
      />
      <div className="bc">
        <Link to={tasksListPath}>{t('nav.tasksAndCases')}</Link>
        <span className="bc-sep">/</span>
        <Link to={reviewCrumbTo}>Create Case Request</Link>
        <span className="bc-sep">/</span>
        Review Before Submission
      </div>
      <div className="pg-title">Review Case Request Details</div>

      <ReviewSection step="Step 01" title="Case Overview" editTo={editTo(1)}>
        <ReviewSubBlock title="Case Categorization">
          <ReviewGrid cols={4}>
            <ReviewField label="File Category" value={draft.fileCategory} />
            <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Case Intake Details">
          <ReviewField label="Description" value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField label="Case Matter" value={caseMatterLabel(draft.caseMatter)} />
            <ReviewField label="Province" value={draft.provinceName || draft.provinceId} />
            <ReviewField label="District" value={draft.districtName || draft.districtId} />
            <ReviewField label="Your Reference Number (External)" value={draft.yourReferenceNumber} />
            <ReviewField label="Other External Reference Number(s)" value={draft.otherExternalReferences} />
            <ReviewField label="Case Number" value={draft.caseNumber} />
            <ReviewField label="Are there any connected matters (Cases)?" value={connectedLabel} />
            {draft.hasConnectedMatters === 'yes' ? (
              <>
                <ReviewField label="Connected Case Number" value={draft.connectedCaseNumber} />
                <ReviewField
                  label="Connected AGD File Reference Number"
                  value={draft.connectedAgdFileRefAvailable ? draft.connectedAgdFileRef : 'Not available'}
                />
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Parties Involved" editTo={editTo(2)}>
        <ReviewSubBlock title="Petitioner Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Name" value={draft.petitionerName} />
            <ReviewField label="NIC / Identification Number" value={draft.nicOrIdentificationNumber} />
            <ReviewField label="Contact Number" value={draft.petitionerContactNumber} />
            <ReviewField label="Email" value={draft.petitionerEmail} />
            <ReviewField label="Address" value={draft.petitionerAddress} fullWidth />
            <ReviewField label="Represented by Attorney" value={draft.representedByAttorney ? 'Yes' : 'No'} />
            {draft.representedByAttorney ? (
              <>
                <ReviewField label="Attorney Name" value={draft.attorneyName} />
                <ReviewField label="Attorney's Contact Number" value={draft.attorneyContactNumber} />
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Respondent Details">
          <ReviewField label="Respondent Name(s)" value={draft.respondentNames} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField
              label="Respondent Type"
              value={apiOrLookupLabel(draft.respondentTypeName, draft.respondentTypeId, lookups.respondentTypes)}
            />
            <ReviewField label="Number of Respondents" value={draft.numberOfRespondents} />
            <ReviewField label="Respondent Address" value={draft.respondentAddress} fullWidth />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Timeline" editTo={editTo(3)}>
        <ReviewSubBlock title="Timeline">
          <ReviewGrid cols={3}>
            <ReviewField
              label="Urgency Level"
              value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
            />
            <ReviewField label="Deadline (if any)" value={formatDisplayDate(draft.deadline)} />
            <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 04" title="Submission & Physical File Delivery" editTo={editTo(4)}>
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Submitted Division/ Unit" value={profile?.externalAgencyName?.trim() || '—'} />
            <ReviewField label="Designation" value={profile?.contactDesignation?.trim() || '—'} />
            <ReviewField label="Submitted by" value={profile?.displayName?.trim() || '—'} />
            <span className="ccr-review-grid-spacer" aria-hidden="true" />
            <ReviewField label="Email Address" value={profile?.email?.trim() || '—'} />
            <ReviewField label="Contact Number" value={profile?.contactNumber?.trim() || '—'} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Physical File Delivery Method">
          <ReviewField label="Delivery Method" value={deliveryLabel} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 05" title="Upload Documents" editTo={editTo(5)}>
        <ReviewSubBlock>
          <CriminalReviewDocumentsTable documents={draft.documents} />
        </ReviewSubBlock>
      </ReviewSection>

      <div className="confirm-section ccr-review-confirm">
        <label className="ccr-chk" id="pp-confirm">
          <input
            type="checkbox"
            checked={draft.confirmCorrect}
            disabled={submitting}
            onChange={(e) => {
              setDraft((d) => ({ ...d, confirmCorrect: e.target.checked }));
              if (e.target.checked) setErrors((er) => ({ ...er, confirmCorrect: '' }));
            }}
          />
          <span className="ccr-review-confirm-text">
            <span className="ccr-review-confirm-main">
              I confirm the above information is correct <span className="req">*</span>
            </span>
            <span className="ccr-review-confirm-hint">Please confirm before submitting this request to AGD.</span>
          </span>
        </label>
        {errors.confirmCorrect ? <div className="ccr-review-confirm-err">{errors.confirmCorrect}</div> : null}
      </div>

      <PublicPetitionWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={5}
        onBack={() => nav(editTo(5))}
        showSubmit
        submitting={submitting}
        onSubmit={() => void submit()}
        reviewMode
      />
    </div>
  );
}
